import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lock, Mail, ShieldCheck, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';

interface Props {
  /** Forced first-login change — hides the surrounding card chrome tweaks. */
  forced?: boolean;
  /** Called after a successful change. */
  onChanged?: () => void;
}

const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,72}$/;

export function ChangePasswordCard({ forced = false, onChanged }: Props) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const sendOtp = useMutation({
    mutationFn: async () => {
      const res = await api.post(API_ENDPOINTS.ADMIN_ACCOUNT.SEND_PASSWORD_OTP);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      setOtpSent(true);
      setError(null);
      toast.success('Verification code sent to your email');
    },
    onError: (e: any) => setError(e?.response?.data?.message || 'Could not send the verification code.'),
  });

  const change = useMutation({
    mutationFn: async () => {
      await api.post(API_ENDPOINTS.ADMIN_ACCOUNT.CHANGE_PASSWORD, {
        currentPassword,
        otp,
        newPassword,
      });
    },
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setCurrentPassword(''); setOtp(''); setNewPassword(''); setConfirm(''); setOtpSent(false);
      toast.success('Password changed successfully');
      onChanged?.();
    },
    onError: (e: any) => setError(e?.response?.data?.message || 'Could not change the password.'),
  });

  const handleSendOtp = () => {
    setError(null);
    if (!currentPassword) { setError('Enter your current password first.'); return; }
    sendOtp.mutate();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!currentPassword) { setError('Enter your current password.'); return; }
    if (!otpSent) { setError('Send the verification code to your email first.'); return; }
    if (otp.trim().length !== 6) { setError('Enter the 6-digit code from your email.'); return; }
    if (!PASSWORD_RULE.test(newPassword)) {
      setError('New password must be at least 8 characters and include a letter and a number.');
      return;
    }
    if (newPassword !== confirm) { setError("New passwords don't match."); return; }
    change.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Change Password
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          For your security, changing your password requires a verification code sent to your email.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          {success && !forced && (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="h-4 w-4" /> Password changed successfully.
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="currentPassword" required>Current Password</Label>
            <Input
              id="currentPassword"
              type="password"
              placeholder="Enter current password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label required>Email Verification</Label>
            <div className="flex gap-2">
              <Input
                placeholder="6-digit code"
                inputMode="numeric"
                maxLength={6}
                leftIcon={<ShieldCheck className="h-4 w-4" />}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                disabled={!otpSent}
              />
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={handleSendOtp}
                isLoading={sendOtp.isPending}
              >
                <Mail className="h-4 w-4 mr-2" />
                {otpSent ? 'Resend' : 'Send code'}
              </Button>
            </div>
            {otpSent && (
              <p className="text-xs text-muted-foreground">
                A 6-digit code was emailed to you. It expires in 10 minutes.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword" required>New Password</Label>
            <Input
              id="newPassword"
              type="password"
              placeholder="Min. 8 chars, with a letter and a number"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" required>Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Repeat new password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          <Button type="submit" isLoading={change.isPending} disabled={!otpSent}>
            Update Password
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
